/**
 * PaymentManager — handles SOL, USDT, and $WOOD token payments.
 *
 * Uses dynamic imports for @solana/web3.js and @solana/spl-token
 * so the Solana SDK is only loaded when the user first pays.
 *
 * Level 1 is FREE — no payment required.
 *
 * Usage:
 *   PaymentManager.payWithSOL(stage)   // sends ~$1 SOL to treasury
 *   PaymentManager.payWithUSDT(stage)  // sends 1 USDT to treasury
 *   PaymentManager.payWithWOOD(stage)  // sends $1-equiv WOOD to treasury
 *   PaymentManager.verifyPayment(sig, stage, token)   // requires X session
 */

import WalletManager from './WalletManager';
import XAuth from './XAuth';

const TREASURY_WALLET = process.env.REACT_APP_TREASURY_WALLET || '';
const USDT_MINT = 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB';
const WOOD_MINT = process.env.REACT_APP_WOOD_MINT || '';
const USDT_DECIMALS = 6;
const WOOD_DECIMALS = 9;
const PAYMENT_AMOUNT_USD = 1; // $1 per level

// Cache Solana modules after first load
let _solWeb3 = null;
let _splToken = null;

async function loadSolana() {
  if (!_solWeb3) {
    _solWeb3 = await import('@solana/web3.js');
  }
  if (!_splToken) {
    _splToken = await import('@solana/spl-token');
  }
  return { web3: _solWeb3, spl: _splToken };
}

function getConnection() {
  const rpc = process.env.REACT_APP_SOLANA_RPC || 'https://api.mainnet-beta.solana.com';
  return new _solWeb3.Connection(rpc, 'confirmed');
}

const PaymentManager = {
  /**
   * Fetch current SOL price in USD from our API.
   */
  async getSolPrice() {
    try {
      const resp = await fetch('/api/payment/sol-price');
      const data = await resp.json();
      return data.price || 0;
    } catch {
      return 0;
    }
  },

  /**
   * Fetch current WOOD token price in USD from our API.
   */
  async getWoodPrice() {
    try {
      const resp = await fetch('/api/payment/wood-price');
      const data = await resp.json();
      return data.price || 0;
    } catch {
      return 0;
    }
  },

  /**
   * Fetch current epoch info (pot size, time remaining, leader).
   */
  async getEpochInfo() {
    try {
      const resp = await fetch('/api/payment/epoch');
      const data = await resp.json();
      return data;
    } catch {
      return null;
    }
  },

  /**
   * Pay with SOL — calculates amount from current price.
   * Returns { signature, amount } on success.
   */
  async payWithSOL(stage) {
    const { web3 } = await loadSolana();
    const provider = WalletManager.getProvider();
    if (!provider) throw new Error('Wallet not connected');
    if (!TREASURY_WALLET) throw new Error('Treasury wallet not configured');

    const solPrice = await this.getSolPrice();
    if (!solPrice || solPrice <= 0) throw new Error('Could not fetch SOL price');

    const solAmount = PAYMENT_AMOUNT_USD / solPrice;
    const lamports = Math.ceil(solAmount * web3.LAMPORTS_PER_SOL);

    const connection = getConnection();
    const fromPubkey = provider.publicKey;
    const toPubkey = new web3.PublicKey(TREASURY_WALLET);

    const transaction = new web3.Transaction().add(
      web3.SystemProgram.transfer({
        fromPubkey,
        toPubkey,
        lamports,
      })
    );

    const { blockhash } = await connection.getLatestBlockhash();
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = fromPubkey;

    const signed = await provider.signTransaction(transaction);
    const signature = await connection.sendRawTransaction(signed.serialize());
    await connection.confirmTransaction(signature, 'confirmed');

    return { signature, amount: solAmount, token: 'SOL', lamports };
  },

  /**
   * Pay with USDT (SPL token transfer).
   * Returns { signature, amount } on success.
   */
  async payWithUSDT(stage) {
    const { web3, spl } = await loadSolana();
    const provider = WalletManager.getProvider();
    if (!provider) throw new Error('Wallet not connected');
    if (!TREASURY_WALLET) throw new Error('Treasury wallet not configured');

    const connection = getConnection();
    const fromPubkey = provider.publicKey;
    const toPubkey = new web3.PublicKey(TREASURY_WALLET);
    const mintPubkey = new web3.PublicKey(USDT_MINT);

    const amount = PAYMENT_AMOUNT_USD * Math.pow(10, USDT_DECIMALS); // 1 USDT = 1_000_000

    // Get or create associated token accounts
    const fromATA = await spl.getAssociatedTokenAddress(mintPubkey, fromPubkey);
    const toATA = await spl.getAssociatedTokenAddress(mintPubkey, toPubkey);

    const transaction = new web3.Transaction();

    // Create destination ATA if it doesn't exist
    try {
      await spl.getAccount(connection, toATA);
    } catch {
      transaction.add(
        spl.createAssociatedTokenAccountInstruction(
          fromPubkey, toATA, toPubkey, mintPubkey
        )
      );
    }

    // Add transfer instruction
    transaction.add(
      spl.createTransferInstruction(fromATA, toATA, fromPubkey, amount)
    );

    const { blockhash } = await connection.getLatestBlockhash();
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = fromPubkey;

    const signed = await provider.signTransaction(transaction);
    const signature = await connection.sendRawTransaction(signed.serialize());
    await connection.confirmTransaction(signature, 'confirmed');

    return { signature, amount: PAYMENT_AMOUNT_USD, token: 'USDT' };
  },

  /**
   * Pay with $WOOD token.
   * Returns { signature, amount } on success.
   */
  async payWithWOOD(stage) {
    if (!WOOD_MINT) throw new Error('WOOD token mint not configured');

    const { web3, spl } = await loadSolana();
    const provider = WalletManager.getProvider();
    if (!provider) throw new Error('Wallet not connected');
    if (!TREASURY_WALLET) throw new Error('Treasury wallet not configured');

    const woodPrice = await this.getWoodPrice();
    if (!woodPrice || woodPrice <= 0) throw new Error('Could not fetch WOOD price');

    const woodAmount = PAYMENT_AMOUNT_USD / woodPrice;
    const rawAmount = Math.ceil(woodAmount * Math.pow(10, WOOD_DECIMALS));

    const connection = getConnection();
    const fromPubkey = provider.publicKey;
    const toPubkey = new web3.PublicKey(TREASURY_WALLET);
    const mintPubkey = new web3.PublicKey(WOOD_MINT);

    const fromATA = await spl.getAssociatedTokenAddress(mintPubkey, fromPubkey);
    const toATA = await spl.getAssociatedTokenAddress(mintPubkey, toPubkey);

    const transaction = new web3.Transaction();

    try {
      await spl.getAccount(connection, toATA);
    } catch {
      transaction.add(
        spl.createAssociatedTokenAccountInstruction(
          fromPubkey, toATA, toPubkey, mintPubkey
        )
      );
    }

    transaction.add(
      spl.createTransferInstruction(fromATA, toATA, fromPubkey, rawAmount)
    );

    const { blockhash } = await connection.getLatestBlockhash();
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = fromPubkey;

    const signed = await provider.signTransaction(transaction);
    const signature = await connection.sendRawTransaction(signed.serialize());
    await connection.confirmTransaction(signature, 'confirmed');

    return { signature, amount: woodAmount, token: 'WOOD' };
  },

  /**
   * Verify a payment on the server side.
   * Server checks the on-chain transaction matches expected params.
   */
  async verifyPayment(signature, stage, token) {
    try {
      const resp = await fetch('/api/payment/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...XAuth.getAuthHeaders() },
        body: JSON.stringify({ signature, stage, token }),
      });
      const data = await resp.json();
      return data;
    } catch {
      return { verified: false, error: 'Network error' };
    }
  },
};

export default PaymentManager;
